import { useDeferredValue, useState } from "react";
import { Search } from "lucide-react";
import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@ndd/ui";
import { RDListRow } from "@/components/v2/rd-list-row";
import type { RdCoverageStatus, RdListItem } from "@/components/v2/rd-list-row";
import { THEMED_BAND, THEMED_CARD, THEMED_EYEBROW } from "@/lib/always-look";
import { cn } from "@/lib/utils";
import {
  ALL_COVERAGE,
  filterRdList,
  normalizeRdName,
  type CoverageFilter,
} from "@/lib/rd-list-filter";

export type {
  RdCoverageStatus,
  RdCoverageSummary,
  RdListItem,
} from "@/components/v2/rd-list-row";
export { normalizeRdName };

const RD_SEARCH_THRESHOLD = 20;

// Under the always theme (NO-792) the table is a white card with a card-white
// header band and mono column labels; the classic table is unchanged.
const HEADER_LABEL = cn(
  "text-xs tracking-wider text-muted-foreground font-medium uppercase",
  THEMED_EYEBROW
);
const COVERAGE_OPTIONS: Array<{
  value: RdCoverageStatus;
  label: string;
}> = [
  { value: "deep", label: "Deep coverage" },
  { value: "building", label: "Building" },
  { value: "thin", label: "Thin" },
  { value: "no_recent", label: "No recent upload" },
];

function coverageDotClass(status: RdCoverageStatus): string {
  switch (status) {
    case "deep":
      return "bg-success";
    case "building":
      return "bg-sky-500";
    case "thin":
      return "bg-sunlight-500";
    case "no_recent":
      return "bg-destructive";
  }
}

const LIST_GRID =
  "md:grid-cols-[minmax(0,2fr)_0.7fr_minmax(0,1.5fr)_minmax(0,1.2fr)_minmax(0,1.4fr)_24px]";
const COLUMN_LABELS = ["RD", "Sessions", "Coverage", "Last Session", "Last Activity"];

export function RDList({ rds }: { rds: RdListItem[] }) {
  const [query, setQuery] = useState("");
  const [coverageFilter, setCoverageFilter] = useState<CoverageFilter>(ALL_COVERAGE);
  const deferredQuery = useDeferredValue(query);
  const showSearch = rds.length > RD_SEARCH_THRESHOLD;
  const filteredRds = filterRdList(rds, deferredQuery, coverageFilter);

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        {showSearch && <RdSearchField query={query} onChange={setQuery} />}
        {showSearch && (
          <CoverageSelect value={coverageFilter} onChange={setCoverageFilter} />
        )}
      </div>

      <RdTable rds={filteredRds} />
    </div>
  );
}

function RdSearchField({
  query,
  onChange,
}: {
  query: string;
  onChange: (next: string) => void;
}) {
  return (
    <div className="relative w-full sm:max-w-md">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-subtle" />
      <Input
        type="text"
        value={query}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Search RDs…"
        aria-label="Search RDs"
        className="h-10 rounded-[10px] border-border bg-card pl-10 pr-3 text-sm shadow-sm placeholder:text-subtle"
      />
    </div>
  );
}

function CoverageSelect({
  value,
  onChange,
}: {
  value: CoverageFilter;
  onChange: (next: CoverageFilter) => void;
}) {
  return (
    <div className="w-full sm:w-56">
      <Select value={value} onValueChange={(next) => onChange(next as CoverageFilter)}>
        <SelectTrigger
          aria-label="Filter by coverage"
          className="h-10 rounded-[10px] border-border bg-card text-sm shadow-sm"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_COVERAGE}>All coverage</SelectItem>
          {COVERAGE_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              <span className="flex items-center gap-2">
                <span
                  className={cn(
                    "h-2 w-2 rounded-full shrink-0",
                    coverageDotClass(option.value)
                  )}
                />
                {option.label}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function RdTable({ rds }: { rds: RdListItem[] }) {
  return (
    <div
      className={cn(
        "rounded-xl border border-border bg-card shadow-sm overflow-hidden",
        THEMED_CARD
      )}
    >
      <div
        className={cn(
          `hidden md:grid ${LIST_GRID} gap-4 py-3 px-5 bg-muted border-b border-border`,
          THEMED_BAND
        )}
      >
        {COLUMN_LABELS.map((label) => (
          <div key={label} className={HEADER_LABEL}>
            {label}
          </div>
        ))}
        <div />
      </div>

      {rds.length > 0 ? (
        rds.map((rd) => <RDListRow key={rd.id} rd={rd} />)
      ) : (
        <div className="px-5 py-10 text-center text-sm text-muted-foreground">
          No RDs match your filters.
        </div>
      )}
    </div>
  );
}
